import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FileTypes } from '@guryou/core';
import {
  Block,
  DefaultText,
  FlexRow,
  HtmlAudioPlayer,
  HtmlVideoPlayer,
  IconButton,
  imageNotFound,
  ImgIcon,
  LeftIcon,
  MediumText,
  PdfIcon,
  PlayIcon,
  RightIcon,
  Spinner,
  VisibilityIconOff,
  VisibilityIconOn,
} from '@guryou/html-components';
import { getCustomerPublicFile, stateIsLoading, useEffectOnSuccess } from '@guryou/redux';

const getPlayerByType = (type, src, handleError, cantPreviewFile) => {
  if (type === FileTypes.video) {
    return <HtmlVideoPlayer src={src} />;
  }
  if (type === FileTypes.audio) {
    return <HtmlAudioPlayer src={src} />;
  }
  if (type === FileTypes.image) {
    return <img src={src} style={{ maxHeight: '500px' }} alt="section img" onError={handleError} />;
  }
  if (type === FileTypes.document) {
    return <DefaultText color="white">{cantPreviewFile}</DefaultText>;
  }
  return <DefaultText color="white">Something went wrong!</DefaultText>;
};

const MyCustomPreviewModal = ({ items, active, providerId, handleFileSeenUnseen, allowPreview }) => {
  const [currentActive, setCurrentActive] = useState(active);
  const [currentItems, setCurrentItems] = useState(() => {
    if (allowPreview) {
      return (items || []).map(x => (x.id === active ? { ...x, seen: true } : x));
    }

    return (items || []).filter(x => x.free);
  });

  const { t } = useTranslation();

  const file = useSelector(state => state.files.getPublic);

  const dispatch = useDispatch();

  const [data, setData] = useState({
    type: null,
    src: null,
  });

  const handleFileSeen = id => {
    handleFileSeenUnseen(id, true);
    setCurrentItems(prev => prev.map(x => (x.id === id ? { ...x, seen: true } : x)));
  };

  const handleError = () => {
    setData({ type: FileTypes.image, src: imageNotFound }); // Set to default image if the original fails
  };

  useEffectOnSuccess(
    state => state.files.getPublic,
    data => {
      setData({ type: data.type, src: data?.signedUrl });
    }
  );

  const handleClick = (id, seen, type) => {
    if (type === FileTypes.document) {
      setData({ type: FileTypes.document, src: '123' });
      setCurrentActive(id);
      return;
    }
    dispatch(getCustomerPublicFile(id, +providerId));
    if (!seen) {
      handleFileSeen(id, true);
    }
    setCurrentActive(id);
  };

  const handleShift = item => {
    if (allowPreview || item.free) {
      if (item.type === FileTypes.document) {
        setData({ type: FileTypes.document, src: '123' });
        setCurrentActive(item.id);
        return;
      }

      dispatch(getCustomerPublicFile(item.id, +providerId));
      if (!item.seen) {
        handleFileSeen(item.id, true);
      }
      setCurrentActive(item.id);
    }
  };

  const handleNext = () => {
    const nextItem = currentItems[currentIndex + 1];
    handleShift(nextItem);
  };

  const handlePrev = () => {
    const prevItem = currentItems[currentIndex - 1];
    handleShift(prevItem);
  };

  if (!data.type || !data.src) {
    return (
      <FlexRow justifyContent="center" alignItems="center" height="500px" width="100%">
        <Spinner />
      </FlexRow>
    );
  }

  const currentItem = currentItems.find(x => x.id === currentActive);
  const currentIndex = currentItems.findIndex(x => x.id === currentActive);

  return (
    <>
      <Block margin={0} padding={0} display="flex" justifyContent="center" alignItems="center">
        <FlexRow justifyContent="center" alignItems="center" minHeight="500px" height="auto" width="95%">
          {stateIsLoading(file) ? <Spinner /> : getPlayerByType(data.type, data.src, handleError, t('VIDEO_COURSE_PAGE.cantPreviewFile'))}
        </FlexRow>
      </Block>
      <FlexRow xs={12} justifyContent="space-between">
        <MediumText xs={10} padding={2} margin={2} color="white">
          {currentItem.title || currentItem.name}
        </MediumText>
        <FlexRow xs={2} justifyContent="space-between" alignItems="center">
          <IconButton color={currentIndex < 1 ? 'gray' : 'lightgray'} disabled={currentIndex < 1} onClick={() => handlePrev()}>
            <LeftIcon />
          </IconButton>
          <IconButton
            color={currentIndex + 1 >= currentItems.length ? 'gray' : 'lightgray'}
            disabled={currentIndex + 1 >= currentItems.length}
            onClick={() => handleNext()}
          >
            <RightIcon />
          </IconButton>
        </FlexRow>
      </FlexRow>

      <FlexRow xs={12}>
        {currentItems.map(x => (
          <Block
            key={x.id}
            xs={12}
            margin={1}
            padding={4}
            color="white"
            border="1px solid lightgray"
            background={x.id === currentActive ? '#343a40' : 'inherit'}
            borderRadius="10px"
            onClick={() => handleClick(x.id, x.seen, x.type)}
          >
            <FlexRow xs={12} alignItems="center" justifyContent="space-between">
              <FlexRow xs={11.6}>
                {x.type === FileTypes.document ? <PdfIcon /> : x.type === FileTypes.image ? <ImgIcon /> : <PlayIcon />}
                <DefaultText marginLeft={2} color="white" padding={0} margin={0}>
                  {x.title || x.name}
                </DefaultText>
              </FlexRow>
              {allowPreview && (x.seen ? <VisibilityIconOn /> : <VisibilityIconOff />)}
            </FlexRow>
          </Block>
        ))}
      </FlexRow>
    </>
  );
};

export default MyCustomPreviewModal;
